/* eslint-disable max-statements */

import { Button, Flex, Modal, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

import { TransitDocumentsEnum } from '@e-origin/shared';

import { deleteIcon, viewIcon } from '@assets';
import { Table, TableActions, TableColumnProps, TableConditionalRowStyles } from '@components';
import { documentsLabels, TransitDocumentEdit } from './TransitDocumenEdit';

interface TransitDocumentsProps {
  disabledDocuments?: TransitDocumentsEnum[];
  disabled: boolean;
  previewDocuments?: Record<string, any[]>;
  documents: Record<string, any[]>;
  onSave: (savedDocument: any) => void;
  onDelete: (documentType: any, documentIndex: number) => void;
  forGoodsItem?: boolean;
  isArrival: boolean;
}

export interface DocumentItemProps {
  type: TransitDocumentsEnum;
  label?: string;
  code: string;
  referenceNumber?: string;
  complementOfInformation?: string;
  dateOfValidity?: string;
  docDetails: {
    type: TransitDocumentsEnum;
    dataSource: any;
    dataSourceIndex: number;
  };
  readonly: boolean;
}

export const TransitDocuments: React.FC<TransitDocumentsProps> = ({
  disabledDocuments = [],
  disabled,
  previewDocuments = {},
  documents = {},
  onSave,
  onDelete,
  forGoodsItem,
  isArrival,
}) => {
  const [data, setData] = useState<DocumentItemProps[]>([]);
  const selectedRow = useRef<DocumentItemProps>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const toggleShowDocumentModal = () => {
    setShowModal(!showModal);

    if (showModal) {
      selectedRow.current = null;
    }
  };

  const toggleShowDeleteDocumentModal = () => {
    setShowDeleteModal(!showDeleteModal);

    if (showDeleteModal) {
      selectedRow.current = null;
    }
  };

  const handleSavingDocument = (savedDocument: any) => {
    onSave(savedDocument);
    selectedRow.current = null;
  };

  const buildRow = (documentType: TransitDocumentsEnum, item: any, itemIdx: number, isPreview = false) => ({
    type: documentType,
    label: documentsLabels[documentType],
    code: item.type || item.code || item.role || '',
    complementOfInformation: item.complementOfInformation || '',
    referenceNumber: item.referenceNumber || item.text || item.vatIdentificationNumber || item.reference?.customsOfficeGuarantee?.referenceNumber || '',
    docDetails: { type: documentType, dataSource: item, dataSourceIndex: itemIdx },
    readonly: isPreview,
  });

  useEffect(() => {
    const tableData: DocumentItemProps[] = [];

    for (const [documentType, docs] of Object.entries(previewDocuments || [])) {
      docs.forEach((item, itemIdx) => tableData.push(buildRow(documentType as TransitDocumentsEnum, item, itemIdx, true)));
    }

    for (const [documentType, docs] of Object.entries(documents || [])) {
      docs.forEach((item, itemIdx) =>
        tableData.push(buildRow(documentType as TransitDocumentsEnum, item, itemIdx, disabledDocuments.includes(documentType as TransitDocumentsEnum))),
      );
    }

    setData(tableData);
  }, [documents]);

  const columns: TableColumnProps<DocumentItemProps>[] = [
    {
      name: 'Type of document',
      cell: (row) => row.label,
    },
    {
      name: 'Code',
      cell: (row) => row.code,
    },
    {
      name: 'Reference',
      cell: (row) => row.referenceNumber,
      width: '250px',
    },
    {
      name: 'Complement of information',
      cell: (row) => row.complementOfInformation,
      width: '250px',
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row: DocumentItemProps) => (
        <TableActions
          actions={[
            {
              icon: viewIcon,
              text: 'View details',
              onClick: () => {
                setShowModal(true);
              },
            },
            {
              if: !row.readonly && !disabled,
              icon: deleteIcon,
              text: 'Delete',
              onClick: () => {
                setShowDeleteModal(true);
              },
            },
          ]}
          onClick={() => {
            selectedRow.current = row;
          }}
        />
      ),
    },
  ];

  const conditionalRowStyles: TableConditionalRowStyles<DocumentItemProps> = {
    when: (row) => row.readonly,
    style: {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  };

  return (
    <Stack style={{ flexShrink: 0 }}>
      <Flex py={12} justify="flex-end">
        {!disabled && (
          <Button onClick={toggleShowDocumentModal} variant="outline" size="lg" mb="xl" data-pw="add-document-btn">
            Add new
          </Button>
        )}
      </Flex>

      <Table columns={columns} dataSource={data || []} conditionalRowStyles={conditionalRowStyles} pagination verticalSpacing="xs" />

      {showModal && (
        <TransitDocumentEdit
          opened={true}
          isArrival={isArrival}
          onHide={toggleShowDocumentModal}
          docDetails={selectedRow.current?.docDetails}
          onSave={handleSavingDocument}
          editDisabled={disabled || selectedRow.current?.readonly}
          disabledDocuments={disabledDocuments}
          forGoodsItem={forGoodsItem}
        />
      )}
      {showDeleteModal && (
        <Modal title="Confirm document delete" opened={showDeleteModal} onClose={toggleShowDeleteDocumentModal} centered zIndex={9999}>
          <Text>Are you sure you want to delete this document?</Text>
          <Button
            color="red"
            mt="md"
            onClick={() => {
              onDelete(selectedRow.current.type, selectedRow.current.docDetails.dataSourceIndex);
              toggleShowDeleteDocumentModal();
            }}
          >
            Delete
          </Button>
        </Modal>
      )}
    </Stack>
  );
};
